import { Stack, Typography } from '@mui/material';
import { AttendanceCode, useGetAttendanceCodesQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { TagSubject } from '@schooly/style';
import { FC } from 'react';
import { EntriesByPoint } from './take-attendance-utils';

export type AttendanceStats = {
  present: number;
  absent: number;
  noStatus: number;
};

export const AttendanceStatsBadge: FC<{
  computeStats: (v: AttendanceCode[]) => AttendanceStats;
}> = ({ computeStats }) => {
  const { schoolId = '' } = useAuth();
  const { data: codes = [] } = useGetAttendanceCodesQuery(schoolId);

  const stats = computeStats(codes);

  const renderValue = (v: number, color: string, withDivider = true) => {
    if (!v) return null;

    return (
      <>
        <Typography color={color}>{v}</Typography>
        {withDivider && <Typography color="text.secondary"> | </Typography>}
      </>
    );
  };

  return (
    <TagSubject
      renderLabel={() => {
        return (
          <Stack flexDirection="row" gap={0.5}>
            {renderValue(stats.present, 'success.main', !!stats.absent || !!stats.noStatus)}
            {renderValue(stats.absent, 'error.main', !!stats.noStatus)}
            {renderValue(stats.noStatus, 'text.secondary', false)}
          </Stack>
        );
      }}
    />
  );
};

const computeAttendanceStats = (
  sessionValues: Array<{ attendance_code_id?: string | null } | null | undefined>,
  codes: AttendanceCode[],
): AttendanceStats => {
  const isCodePresent = (codeId: string) => !!codes.find((c) => c.id === codeId)?.is_present;

  return sessionValues.reduce<AttendanceStats>(
    (acc, row) => {
      if (!row?.attendance_code_id) {
        acc.noStatus++;
      } else {
        isCodePresent(row.attendance_code_id) ? acc.present++ : acc.absent++;
      }

      return acc;
    },
    { present: 0, absent: 0, noStatus: 0 },
  );
};

export const mapTakeAttendanceListRowToAttendanceStats =
  ({ pointKey, rows }: { rows: Array<{ entries: EntriesByPoint }>; pointKey: string }) =>
  (codes: AttendanceCode[]) =>
    computeAttendanceStats(
      rows.map((r) => r.entries[pointKey]),
      codes,
    );
